import React from 'react';
import classnames from 'classnames/bind';
import { DateTime } from 'luxon';
import { Link, To } from 'react-router-dom';

import { IconType } from 'types/icon';
import { RELATED_ENTITIES_KEYS } from 'types/event';
import { SystemEventsWidget as SystemEventsWidgetType } from 'types/dashboard_widget';

import { useCameras } from 'cameras/hooks/useCameras';
import { useDeployments } from 'deployments/hooks/useDeployments';
import { useFiles } from 'files/hooks/useFiles';
import { useGateways } from 'gateways/hooks/useGateways';
import { useHasAccess } from 'hooks/useHasAccess';
import { useEvents } from 'events/hooks/useEvents';
import { useStreams } from 'streams/hooks/useStreams';
import { useDashboardFilterParams } from 'dashboards/hooks/useDashboardFilterParams';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';

import { Card } from 'components/Card/Card';
import { Code } from 'components/Code/Code';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { Icon } from 'components/Icon/IconDeprecated';
import { Pagination } from 'components/Pagination/Pagination';
import { Skeleton } from '@mantine/core';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';
import { Text } from 'components/Text/Text';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './SystemEventsWidget.module.scss';

const c = classnames.bind(styles);

export function SystemEventsWidget(widget: SystemEventsWidgetType) {
  const limit = widget.limit ?? 10; // Page size
  const { options = {} } = widget;
  const { gateway_id, stream_ids: dashboardStreamIds = [] } =
    useDashboardFilterParams();
  const { start, stop, timezone } = useTimeRangeParams();

  const [page, setPage] = React.useState(1);

  // Merge widget options with dashboard filters
  const gatewayIdsFilter = gateway_id
    ? Array.from(new Set([gateway_id, ...(options.gateway_ids || [])]))
    : options.gateway_ids;

  const streamIdsFilter = Array.from(
    new Set([...dashboardStreamIds, ...(options.stream_ids || [])])
  );

  // Map entity IDs to object_ids and object_types for API
  const objectIds: string[] = [];
  const objectTypesSet = new Set<string>();

  if (options.camera_ids?.length) {
    objectIds.push(...options.camera_ids);
    objectTypesSet.add('camera');
  }

  if (options.gateway_ids?.length || gatewayIdsFilter?.length) {
    const allGatewayIds = Array.from(
      new Set([...(options.gateway_ids || []), ...(gatewayIdsFilter || [])])
    );
    objectIds.push(...allGatewayIds);
    objectTypesSet.add('gateway');
  }

  if (options.stream_ids?.length || streamIdsFilter.length > 0) {
    const allStreamIds = Array.from(
      new Set([...(options.stream_ids || []), ...streamIdsFilter])
    );
    objectIds.push(...allStreamIds);
    objectTypesSet.add('stream');
  }

  if (options.deployment_ids?.length) {
    objectIds.push(...options.deployment_ids);
    objectTypesSet.add('deployment');
  }

  const objectTypes = Array.from(objectTypesSet);

  const {
    data: eventsData,
    isLoading: isLoadingEvents,
    error,
  } = useEvents({
    params: {
      page,
      limit,
      since: start,
      until: stop,
      event_types: options.event_types?.length
        ? options.event_types
        : undefined,
      severities: options.severities?.length ? options.severities : undefined,
      object_ids:
        objectIds.length > 0 ? Array.from(new Set(objectIds)) : undefined,
      object_types: objectTypes.length > 0 ? objectTypes : undefined,
    },
    keepPreviousData: true,
  });

  React.useEffect(() => {
    if (page !== 1 && error && error.code === 'invalid-pagination-parameters') {
      setPage(1);
    }
  }, [error, page]);

  const events = eventsData?.data ?? [];

  const ids: Record<string, Array<string>> = Object.fromEntries(
    RELATED_ENTITIES_KEYS.map((key) => [`${key}s`, []])
  );

  if (events) {
    events.forEach((event) => {
      Object.entries(event.related_entities).forEach(([key, value]) => {
        if (value && !ids[`${key}s`].includes(value)) {
          ids[`${key}s`].push(value);
        }
      });
    });
  }

  const {
    camera_ids,
    deployment_ids,
    file_ids,
    gateway_ids,
    stream_ids: eventStreamIds,
  } = ids;

  const { data: cameras, isInitialLoading: isLoadingCameras } = useCameras({
    params: { camera_ids, limit: camera_ids.length },
    enabled: camera_ids.length > 0,
  });

  const { data: deployments, isInitialLoading: isLoadingDeployments } =
    useDeployments({
      params: { deployment_ids, limit: deployment_ids.length },
      enabled: deployment_ids.length > 0,
      keepPreviousData: true,
    });

  const { data: files, isInitialLoading: isLoadingFiles } = useFiles({
    params: { file_ids, limit: file_ids.length },
    enabled: file_ids.length > 0,
  });

  const { data: gateways, isInitialLoading: isLoadingGateways } = useGateways({
    params: {
      gateway_ids,
      limit: gateway_ids.length,
    },
    enabled: gateway_ids.length > 0,
  });

  const { data: streams, isInitialLoading: isLoadingStreams } = useStreams({
    params: { stream_ids: eventStreamIds, limit: eventStreamIds.length },
    enabled: eventStreamIds.length > 0,
    keepPreviousData: true,
  });

  const isLoading =
    isLoadingEvents ||
    isLoadingCameras ||
    isLoadingDeployments ||
    isLoadingFiles ||
    isLoadingGateways ||
    isLoadingStreams;

  if (isLoading) {
    return (
      <Widget widget={widget}>
        <Card className={c('events')}>
          <SkeletonList
            component={
              <li className={c('event')}>
                <div className={c('timestamp')}>
                  <Skeleton height="3rem" width="5rem" />
                </div>
                <div className={c('info')}>
                  <div className={'icon'}>
                    <Skeleton height="1rem" width="1rem" radius="1rem" />
                  </div>
                  <div className={c('payload')}>
                    <Skeleton height="1rem" width="80%" />
                  </div>
                  <div className={c('entities')}>
                    <Skeleton height="1rem" width="50%" />
                  </div>
                </div>
                <div>
                  <Skeleton height="1.5rem" width="10rem" />
                </div>
              </li>
            }
            min={limit}
            max={limit}
          />
        </Card>
      </Widget>
    );
  }

  if (events && events.length === 0) {
    return (
      <Widget widget={widget}>
        <Card className={c('events')}>
          <EmptyView>No events recorded yet.</EmptyView>
        </Card>
      </Widget>
    );
  }

  if (!events) {
    return null;
  }

  return (
    <Widget widget={widget}>
      <Card className={c('events')}>
        <ul className={c('list')}>
          {events.map((event) => {
            const { related_entities } = event;

            const camera =
              related_entities.camera_id &&
              cameras?.data.find(({ id }) => id === related_entities.camera_id);

            const deployment =
              related_entities.deployment_id &&
              deployments?.data.find(
                ({ id }) => id === related_entities.deployment_id
              );

            const file =
              related_entities.file_id &&
              files?.data.find(({ id }) => id === related_entities.file_id);

            const gateway =
              related_entities.gateway_id &&
              gateways?.data.find(
                ({ id }) => id === related_entities.gateway_id
              );

            const stream =
              related_entities.stream_id &&
              streams?.data.find(({ id }) => id === related_entities.stream_id);

            return (
              <li key={event.id} className={c('event')}>
                <div className={c('timestamp')}>
                  {DateTime.fromISO(event.event_ts)
                    .setZone(timezone)
                    .toLocaleString(DateTime.DATE_MED)}
                  <br />
                  {DateTime.fromISO(event.event_ts)
                    .setZone(timezone)
                    .toLocaleString(DateTime.TIME_SIMPLE)}
                </div>
                <div className={c('info')}>
                  <div className={'icon'}>
                    <StatusIndicator
                      className={'icon'}
                      status={event.severity}
                      size="small"
                    />
                  </div>
                  <Text className={c('payload')} inline>
                    {event.payload}
                  </Text>
                  <div className={c('entities')}>
                    {camera && (
                      <EntityLink
                        to={`deploy/cameras/${camera.id}`}
                        name={camera.name}
                        icon="camera"
                      />
                    )}
                    {deployment && (
                      <EntityLink
                        to={`deploy/deployments/${deployment.id}`}
                        name={deployment.name || ''}
                        icon="deployment"
                      />
                    )}
                    {file && (
                      <div className={c('center')}>
                        <Icon name="file" size="small" />
                        <span>{file.name}</span>
                      </div>
                    )}
                    {gateway && (
                      <EntityLink
                        to={`deploy/gateways/${gateway.id}`}
                        name={gateway.name}
                        icon="gateway"
                      />
                    )}
                    {stream && (
                      <EntityLink
                        to={`deploy/streams/${stream.id}`}
                        name={stream.name}
                        icon="stream"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Code size="small">{event.event_type}</Code>
                </div>
              </li>
            );
          })}
        </ul>
        {eventsData && eventsData.total_pages > 1 && (
          <Pagination
            className={c('pagination')}
            total={eventsData.total_pages}
            value={page}
            onChange={setPage}
            siblings={0}
          />
        )}
      </Card>
    </Widget>
  );
}

type EntityLinkProps = {
  to: To;
  name: string;
  icon: IconType;
};

function EntityLink({ to, name, icon }: EntityLinkProps) {
  const [hasAccess] = useHasAccess();

  if (hasAccess('deploy')) {
    return (
      <Link to={to}>
        <div className={c('center')}>
          <Icon name={icon} size="small" />
          <span className={c('entity-link')}>{name}</span>
        </div>
      </Link>
    );
  }

  return (
    <div className={c('center')}>
      <Icon name="stream" size="small" />
      <span>{name}</span>
    </div>
  );
}
